import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import SelectList from '../SelectList/SelectList';
import TransitionGroup from '../TransitionGroup/TransitionGroup';
import createFakeEvent from '../../lib/createFakeEvent';
import datasetFromProps from '../../lib/datasetFromProps';
import Notice from '../Notice/Notice';
import { isNil, isObject } from '../../lib/utils';

/**
 * `Input` is an extension of the HTML `input` element with additional support for autocompletion, icons, required indicator
 * and error message. The component accepts any prop supported by the HTML `input` component in addition to the props listed below.
 *
 * The autocompletion data set can be either array of `strings` or array of `objects`. If you use objects, your *onChange*
 * handler will receive the `object` from the `options` provided when autocompletion is used, and `string` when the user
 * types something into the field. If your options are of type `array` of strings, you will always get string datatype in
 * your events.
 *
 * The autocomplete options will be listed using a {@link SelectList} component. Options will be listed in a flat list by
 * default, but if *options* is a tree structure of nested objects you may use the *nestedKey* prop to specify the name of
 * the property name that holds an object's children. This will make display the options in a tree like structure. Advanced
 * users can also use the *nestedKeySelectable* prop to point to a boolean value in the object. If this property is not
 * `true`, the option will not be selectable.
 *
 * The component accepts strings, numbers and objects. If you provide an object, the component will get the key from
 * *labelKey* as value.
 *
 * The `Input` component is used as base for for a lot of other components. See list below.
 *
 * @property {string} autoComplete If set to "off", all auto complete in the browser will be turned off. Defaults to
 * "off" if the component has *options* or a custom *dropdown*. Otherwise it defaults to "on".
 * @property {string} characterCountText The character count label. Defaults to "#".
 * @property {node} children If children is passed to this component, they will be inserted before the input field.
 * @property {string} className The class name of this component. Will be appended to "core-input". The component will also
 * add the following classes depending on the props passed to it: "disabled", "error", "no-label", "no-underline" and
 * "show-search" depending on the state of the component.
 * @property {boolean} clearable If set to `true`, a **Clear** button will be shown on hover and focus. Defaults to `true`.
 * If you are embedding this component in a higher order component and want to control the visibility you can do that by
 * setting the *showClear* prop.
 * @property {string} defaultValue The default value of this component.
 * @property {boolean} disabled The component will be disabled if this prop is set to `true`. Defaults to `false`.
 * @property {node} dropdown Sets a custom dropdown component. This can be used to e.g. render a {@link Tree} component as
 * the dropdown instead of the standard list dropdown.
 * @property {node} errorMessage An error message that will be shown below the input field. Defaults to no error message.
 * @property {boolean} filterOptions If set to `false`, the options will not be filtered based on the value of the input
 * field. Defaults tot `true`.
 * @property {string} iconName Custom icon that will be displayed to the right in the input field. The icon name can be any
 * of the embedded icons in {@link Icon}.
 * @property {string} id The ID of this component. Defaults to a variant of the label provided.
 * @property {node} infoMessage A message that will be shown below the input field. Defaults to no message.
 * @property {string} label The label of the input field. Defaults to no label.
 * @property {string} labelKey The key to used to find the display value if the autocomplete data provided is an array of objects. Defaults to "label".
 * @property {boolean} locked The input field will be disabled and show a small lock icon to the right of the input field.
 * *onUnlock* will be called if the user clicks on the lock icon.
 * @property {number} maxLength The maximum number of characters that can be entered into this input. Defaults to unlimited
 * length.
 * @property {string} nestedKey The name of the property of your children array for each option object. Setting this prop
 * will result in the option list being displaying as a tree structure.
 * @property {string} nestedKeySelectable A named property in your objects in *options* which determines whether or not an
 * option is selectable or not.
 * @property {function} onBlur A callback that will be called when the component has lost focus.
 * @property {function} onChange The change listener that will be called whenever the user changes the input value. If
 * autocompletion is used and *options* is an array of objects, the selected object will be passed as `e.target.value` in the
 * event. If *options* is an array of strings, the string will be passed as if it was typed in manually by the user.
 * If the source of the change is the **Clear** button, the event's type will be "clear". If the source of the change is the
 * search button, the event's type will be "search".
 * @property {function} onFocus A callback that is called when the component gets the input focus.
 * @property {array} options An array of strings or objects that should be used for autocompletion. Supports both array of
 * strings and objects. See *labelKey* and *valueKey*. Defaults to no auto completion options.
 * @property {function} onUnlock A callback that is called whenever the user has clicked on the lock icon. You can use this
 * to unlock all locked fields in a form.
 * @property {string} placeholder The placeholder text for this input field. Defaults to no placeholder.
 * @property {boolean} readOnly Makes this input field read only. A read only input field will have a transparent background.
 * You may use *placeholder* as a default value that will be displayed when the actual value is `undefined`.
 * @property {boolean} required If set to `true`, a required indicator will be displayed inside the input field. Defaults to
 * `false`.
 * @property {boolean} showCharacterCount If set to true, it will show the character count on text input field types. Defaults to `false`.
 * @property {boolean} showClear If *clearable* is set to `true`, but you want better control over the visibility of the
 * clear component, you can use this prop to control the visibility of the button. E.g. {@link TagInput} shows the clear
 * button if there are tags even if the input field itself is empty even though `Input` would not show the **Clear** button
 * in this case.
 * @property {boolean} showOptionsOnFocus By default autocompletion is only shown when the user has typed something. Setting
 * this prop to `true` will result in the autocompletion dropdown to be displayed when the input field gets the focus.
 * Defaults to `false`.
 * @property {boolean} showSearch If set to `true`, a search icon is displayed to the left of the input field. No search icon
 * is displayed by default.
 * @property {boolean} showThousandSeparator If set to `true` and *type*="number" large numbers will be displayed with the
 * thousand separator defined by the browser's locale. The formatting will only be displayed when the input field does not
 * have the input focus in order to not interfere with the value input. **NOTE**: Thousand separator is not applied in
 * Firefox as it sends out a blur event when changing the input type which prevents the field from getting the input focus.
 * Defaults to `false`.
 * @property {boolean} showUnderline The component will display a line under the input field to indicate input focus. You
 * can set the value of this prop to `false` to turn this off. Defaults to `true`.
 * @property {boolean} sortOptions If `true`, the options will be sorted alphabetically by label or value. Defaults to
 * `true`.
 * @property {node} successMessage A success message that will be shown below the input field. Defaults to no message.
 * @property {string} title A value which is used as an HTML title attribute for the input field.
 * @property {string} type The type of this input field. Accepts all types supported by the HTML5 input field. Defaults to
 * the HTML5 input field's default type.
 * @property {string|number|object} value The input value. Supports strings, numbers and objects. If the value is an object
 * the object's `label` attribute will be used as value. You can override this using the *labelKey* prop.
 * @property {string} valueKey The property name that defines the value for your objects in *options*. Defaults to "value".
 * @property {node} warningMessage A warning message that will be shown below the component. Defaults to no warning message.
 *
 * @see DateInput, DateTimeInput, Select, Stopwatch, TagInput, TimeInput
 */
export default class Input extends PureComponent {

  static displayName = 'Input';

  static propTypes = {
    autoComplete: PropTypes.string,
    characterCountText: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    clearable: PropTypes.bool,
    'data-preventmenuclose': PropTypes.bool,
    defaultValue: PropTypes.string, // eslint-disable-line
    disabled: PropTypes.bool,
    dropdown: PropTypes.node,
    errorMessage: PropTypes.node,
    errors: PropTypes.object, // obsolete
    filterOptions: PropTypes.bool,
    iconName: PropTypes.string,
    id: PropTypes.string,
    infoMessage: PropTypes.node,
    itemRenderer: PropTypes.func,
    label: PropTypes.string,
    labelKey: PropTypes.string, // eslint-disable-line
    locked: PropTypes.bool,
    maxLength: PropTypes.number,
    nestedKey: PropTypes.string,
    nestedKeySelectable: PropTypes.string,
    onBlur: PropTypes.func,
    onClear: PropTypes.func,
    onClearNotice: PropTypes.func,
    onClick: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onUnlock: PropTypes.func,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    showCharacterCount: PropTypes.bool,
    showClear: PropTypes.bool,
    showOptionsOnFocus: PropTypes.bool,
    showSearch: PropTypes.bool,
    showThousandSeparator: PropTypes.bool,
    showUnderline: PropTypes.bool,
    sortOptions: PropTypes.bool,
    successMessage: PropTypes.node,
    title: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line
    valueKey: PropTypes.string,
    warningMessage: PropTypes.node,
  };

  static defaultProps = {
    characterCountText: '#',
    clearable: true,
    filterOptions: true,
    labelKey: 'label',
    showUnderline: true,
    sortOptions: true,
    value: '',
    valueKey: 'value',
  };

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidUpdate(prevProps) {
    if (this.state.isFocused && this.props.value && this.props.options !== prevProps.options) {
      const options = this.getFilteredOptions(this.props.value, this.props);

      if (options && options.length > 0 && options.indexOf(this.props.value) === -1) {
        this.setState({ showOptions: true });
      }
    }
  }

  closeDropdown = () => {
    this.setState({ isFocused: false, showOptions: false });
  }

  shouldIgnoreOnBlur = (e = {}, force) => {
    const { dropdown } = this.props;
    return e &&
      !force &&
      dropdown &&
      React.isValidElement(dropdown) &&
      dropdown.props['data-preventmenuclose'] &&
      e.type === 'change';
  }

  onInputBlur = (e = {}, force) => {
    if (!this.shouldIgnoreOnBlur(e, force)) {
      this.setState({ isFocused: false });
    }
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }

  onInputClick = (e) => {
    if (this.props.onClick) {
      this.props.onClick(e);
    }
  }

  onAutocomplete = e => {
    const { onChange, showOptionsOnFocus } = this.props;

    if (!showOptionsOnFocus) {
      this.setState({ showOptions: false });
      setTimeout(this.focus, 50);
    } else {
      this.setState({ isFocused: false, showOptions: false });
    }
    if (onChange) {
      onChange(createFakeEvent(e.target.value, e, datasetFromProps(this.props)), true);
    }
  };

  onBlur = (e = {}, force) => {
    const ignoreOnBlur = this.shouldIgnoreOnBlur(e, force);
    if (!ignoreOnBlur && e) {
      this.setState({ isFocused: false, showOptions: false });
    } else if (!ignoreOnBlur) {
      this.setState({ showOptions: false });
    }
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  onChange = e => {
    const { maxLength, onChange, dropdown, options } = this.props;

    if (!dropdown && options) {
      this.setState({
        showOptions: this.getFilteredOptions(e.target.value).length > 0,
      });
    }
    if (onChange) {
      let value = e.target.value;

      if (maxLength && value.length > maxLength) {
        value = value.substr(0, maxLength);
      }
      onChange(createFakeEvent(value, e, datasetFromProps(this.props)));
    }
  };

  onClear = e => {
    const { onChange, onClear } = this.props;
    const event = createFakeEvent('', e, datasetFromProps(this.props));

    e.preventDefault();
    this.refs.input.value = '';
    if (onChange) {
      event.type = 'clear';
      onChange(event);
    }
    if (onClear) {
      onClear(event);
    }
  };

  onCloseModal = e => {
    e.stopPropagation();
    this.onBlur(e, true);
  };

  onFocus = e => {
    const { disabled, onFocus, options, dropdown, showOptionsOnFocus } = this.props;
    if (!disabled) {
      this.setState({
        isFocused: true,
        showOptions: (dropdown || options) && showOptionsOnFocus,
      });
      if (onFocus) {
        onFocus(e);
      }
    }
  };

  onSelectListKeyDown = e => {
    if (e.key === 'Enter') {
      this.onSearch(e);
    } else if (e.key === 'Escape') {
      this.setState({ showOptions: false });
      if (this.onBlur) {
        this.onBlur(e);
      }
    }
  };

  onSearch = e => {
    this.setState({ showOptions: false });
    if (this.props.onChange) {
      const event = createFakeEvent(this.props.value, e, datasetFromProps(this.props));

      event.type = 'search';
      this.props.onChange(event);
    }
    // Hack! Bubbling events causes issues in legacy modules
    if (e.nativeEvent) {
      e.nativeEvent.stopPropagation();
      e.nativeEvent.preventDefault();
    }
    e.preventDefault();
    e.stopPropagation();
    this.focus();
  };

  onUnlock = e => this.props.onUnlock && !this.props.disabled && this.props.onUnlock(e);

  getDOMNode = () => this.refs.input;

  sortList = (list, labelKey, nestedKey, sortOptions) => {
    const sortedList = list.sort((a, b) => {
      if (typeof a === 'object') {
        return a[labelKey].localeCompare(b[labelKey]);
      }
      return a.localeCompare(b);
    });
    list.forEach(listItem => {
      if (listItem[nestedKey]) {
        sortedList[nestedKey] = { ...this.sortList(listItem[nestedKey], labelKey, nestedKey, sortOptions) };
      }
    });
    return sortedList;
  };

  getFilteredOptions = (input = '', props = this.props) => {
    const { filterOptions, labelKey, nestedKey, options, sortOptions } = props;
    let list = options;

    if (input) {
      if (typeof input === 'object') {
        input = input[labelKey]; // eslint-disable-line no-param-reassign
      } else {
        input = `${input}`; // eslint-disable-line no-param-reassign
      }
      input = input.toLowerCase().trim(); // eslint-disable-line no-param-reassign
    }
    if (filterOptions && input) {
      list = SelectList.filterOptions(options, input, labelKey, nestedKey);
    }
    if (sortOptions) {
      this.sortList(list, labelKey, nestedKey, sortOptions);
    }
    return list;
  };

  blur = () => this.refs.input.blur();

  focus = () => this.refs.input?.focus();

  getDataset = () => this.refs.input.dataset;

  moveCaretToEnd = () => {
    this.refs.input.value = '';
    this.refs.input.value = this.props.value;
  };

  getStringValue = () => {
    const { options, labelKey, value, valueKey, nestedKey, showThousandSeparator, type } = this.props;
    const { isFocused } = this.state;

    let stringValue = isObject(value) ? value[labelKey] : value;

    if (options && typeof options[0] === 'object') {
      const found = SelectList.findOptionById(options, value, valueKey, nestedKey);

      stringValue = found ? found[labelKey] : (value && value[labelKey] !== undefined ? value[labelKey] : value); // eslint-disable-line no-nested-ternary
    }
    if (stringValue === null) {
      stringValue = '';
    }
    if (showThousandSeparator && type === 'number' && Intl && !window.InstallTrigger) {
      stringValue = (!isFocused && stringValue && new Intl.NumberFormat().format(stringValue)) || stringValue;
    }
    return stringValue;
  };

  getDropdownStyle = () => {
    if (this.refInputWrapper) {
      const rect = this.refInputWrapper.getBoundingClientRect();

      return {
        top: rect.bottom + 1,
        left: rect.left,
        width: rect.right - rect.left,
      };
    }
    return undefined;
  };

  createDefaultDropdown = () => {
    const {
      disabled,
      dropdown,
      itemRenderer,
      labelKey,
      nestedKey,
      nestedKeySelectable,
      options,
      value,
      valueKey,
    } = this.props;
    const showOptions = this.state;

    if (options && options.length && !dropdown) {
      if (showOptions && !disabled) {
        const items = this.getFilteredOptions(value);

        if (items.length > 0) {
          return (
            <SelectList
              closeDropdown={this.closeDropdown}
              defaultKeyHandler
              input={this.refs.input}
              isFocused
              itemRenderer={itemRenderer}
              labelKey={labelKey}
              nestedKey={nestedKey}
              nestedKeySelectable={nestedKeySelectable}
              onBlur={this.onSelectListBlur}
              onChange={this.onAutocomplete}
              onKeyDown={this.onSelectListKeyDown}
              options={items}
              style={this.getDropdownStyle()}
              value={value}
              valueKey={valueKey}
            />
          );
        }
      }
    }
    return null;
  };

  createCustomDropdown = () => React.cloneElement(this.props.dropdown, { style: this.getDropdownStyle() });

  getLength = (value = '') => {
    const stringValue = value.toString();
    const { showThousandSeparator, type } = this.props;
    if (stringValue) {
      return (showThousandSeparator && type === 'number') ?
        stringValue.replace(/,/g, '').length :
        stringValue.length;
    }
    return 0;
  }

  createDropdown = () => {
    const { dropdown, options } = this.props;

    if (options && options.length && !dropdown) {
      return this.createDefaultDropdown();
    } else if (dropdown) {
      return this.createCustomDropdown();
    }
    return null;
  };

  render() {
    const {
      autoCapitalize,
      autoComplete,
      autoCorrect,
      characterCountText,
      children,
      className,
      clearable,
      disabled,
      dropdown,
      errorMessage,
      errors,
      filterOptions,
      iconName,
      id,
      infoMessage,
      itemRenderer,
      label,
      labelKey,
      locked,
      maxLength,
      nestedKey,
      nestedKeySelectable,
      onBlur,
      onClear,
      onClearNotice,
      onUnlock,
      options,
      showCharacterCount,
      showClear,
      showOptionsOnFocus,
      showSearch,
      showThousandSeparator,
      showUnderline,
      sortOptions,
      successMessage,
      title,
      value,
      valueKey,
      warningMessage,
      ...rest
    } = this.props;
    let { type } = this.props;
    const isDisabled = disabled || locked;
    const { isFocused, showOptions } = this.state;
    const stringValue = this.getStringValue();
    const isClearVisible = clearable && (showClear !== undefined ? showClear : (stringValue !== undefined && `${stringValue}`.length > 0));
    const materialClass = cx(
      'core-input',
      className,
      {
        focus: isFocused && !disabled,
        error: (errors),
        locked,
        'has-errors': errorMessage || warningMessage || successMessage || infoMessage,
        'no-label': !this.props.label,
        'no-underline': !showUnderline,
        'show-options': showOptions,
        'show-search': showSearch,
        clearable: isClearVisible,
        disabled: isDisabled,
        'read-only': this.props.readOnly,
      }
    );
    const inputId = id || (label && label.toLowerCase().replace(/ /, '-'));
    const labelElement = label ? <label title={title} htmlFor={inputId}>{label}</label> : undefined;
    const actualDropdown = this.createDropdown();
    const inputLength = this.getLength(stringValue);

    // TODO remove !window.InstallTrigger when Firefox no longer sends blur events when changing input type
    if (showThousandSeparator && type === 'number' && Intl && !window.InstallTrigger) {
      type = isFocused ? type : undefined;
    }

    const fallBackAutoValue = isNil(actualDropdown) ? 'on' : 'off';

    const input = (
      <input
        {...rest}
        autoCapitalize={autoCapitalize || fallBackAutoValue}
        autoComplete={autoComplete || fallBackAutoValue}
        autoCorrect={autoCorrect || fallBackAutoValue}
        dir="auto"
        disabled={isDisabled}
        id={inputId}
        onBlur={this.onInputBlur}
        onClick={this.onInputClick}
        onChange={this.onChange}
        onFocus={this.onFocus}
        ref="input"
        tabIndex="0"
        title={title || stringValue}
        type={type}
        value={stringValue || ''}/>
    );
    let content = input;

    if (children) {
      content = (
        <div className="children-wrapper" data-preventmenuclose={this.props['data-preventmenuclose']}>
          {children}
          {input}
        </div>
      );
    }
    return (
      <div className={materialClass} data-preventmenuclose={this.props['data-preventmenuclose']}>
        {labelElement}
        {showCharacterCount && !locked && <span className="char-count">{characterCountText}{inputLength}{maxLength ? ` / ${maxLength}` : ''}</span>}
        <div className={cx("input-wrapper", { 'focused': isFocused })} ref={c => (this.refInputWrapper = c)}>
          {showSearch && <Button className="search-button" iconName="search" onClick={this.onSearch} disabled={isDisabled} />}
          {content}
          {isClearVisible && <Icon name="close" className="input-icon clear" onMouseDown={this.onClear} />}
          {iconName && <Icon name={iconName} className="input-icon" />}
          {locked && <Icon name="lock" title="Click to unlock field" onClick={this.onUnlock} />}
          {this.props.required && <div aria-label="required" className="required">*</div>}
        </div>
        {showOptions && actualDropdown && <div className="input-select-list-modal" onClick={this.onCloseModal} />}
        <div className="input-options">
          <TransitionGroup in={showOptions} className="animate-input-options">
            {actualDropdown || <span/>}
          </TransitionGroup>
        </div>
        <Notice
          errorMessage={errorMessage}
          warningMessage={warningMessage}
          successMessage={successMessage}
          infoMessage={infoMessage}
          onClear={onClearNotice}
          />
      </div>
    );
  }

}